if (!customElements.get('sticky-product-bar')) {
  customElements.define(
    'sticky-product-bar',
    class StickyProductBar extends HTMLElement {
      connectedCallback() {
        if (this.closest('quick-add-modal')) return;
        this.form = document.getElementById(this.dataset.formId);
        this.sourceButton = this.form?.querySelector('[name="add"]');
        if (!this.sourceButton) return;

        this.button = this.querySelector('button');
        this.buttonText = this.button.querySelector('span');
        this.spinner = this.button.querySelector('.loading__spinner');
        this.errorMessage = this.querySelector('.sticky-product-bar__error');
        this.sourceError = this.form.closest('product-form').querySelector('.product-form__error-message-wrapper');
        this.mobile = window.matchMedia('(max-width: 749px)');
        this.queueVisibilityUpdate = this.queueVisibilityUpdate.bind(this);
        this.onViewportChange = this.onViewportChange.bind(this);
        this.mobile.addEventListener('change', this.onViewportChange);

        // Mirror Dawn's state; the button's form attribute submits that same form,
        // including its selected variant, quantity, properties and cart handling.
        this.stateObserver = new MutationObserver(() => this.syncButton());
        this.stateObserver.observe(this.sourceButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'aria-haspopup', 'class'],
          childList: true,
          characterData: true,
          subtree: true,
        });
        if (this.sourceError) {
          this.stateObserver.observe(this.sourceError, {
            attributes: true,
            attributeFilter: ['hidden'],
            childList: true,
            characterData: true,
            subtree: true,
          });
        }

        this.resizeObserver = new ResizeObserver(() => {
          if (!this.hidden && this.mobile.matches) {
            document.body.style.setProperty('--sticky-product-bar-height', `${this.offsetHeight}px`);
          }
          this.queueVisibilityUpdate();
        });
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(this.closest('product-info'));
        this.syncButton();
        this.setAttribute('data-enabled', '');
        this.onViewportChange();
      }

      disconnectedCallback() {
        this.mobile?.removeEventListener('change', this.onViewportChange);
        window.removeEventListener('scroll', this.queueVisibilityUpdate);
        window.removeEventListener('resize', this.queueVisibilityUpdate);
        cancelAnimationFrame(this.frame);
        this.stateObserver?.disconnect();
        this.resizeObserver?.disconnect();
        if (this.hasAttribute('data-enabled')) document.body.style.removeProperty('--sticky-product-bar-height');
      }

      onViewportChange() {
        window.removeEventListener('scroll', this.queueVisibilityUpdate);
        window.removeEventListener('resize', this.queueVisibilityUpdate);
        if (this.mobile.matches) {
          window.addEventListener('scroll', this.queueVisibilityUpdate, { passive: true });
          window.addEventListener('resize', this.queueVisibilityUpdate, { passive: true });
        }
        this.queueVisibilityUpdate();
      }

      queueVisibilityUpdate() {
        if (this.frame) return;
        // Also handles anchor jumps past the button and restored scroll positions.
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          const rect = this.sourceButton.getBoundingClientRect();
          const hidden = !this.mobile.matches || !rect.height || rect.bottom > 0;
          if (this.hidden !== hidden) this.hidden = hidden;
        });
      }

      syncButton() {
        const disabled = this.sourceButton.disabled;
        const busy = this.sourceButton.getAttribute('aria-disabled') === 'true';
        const loading = this.sourceButton.classList.contains('loading');
        const label = disabled
          ? this.sourceButton.querySelector('span').textContent.trim()
          : this.button.dataset.addLabel;

        this.button.disabled = disabled;
        this.button.setAttribute('aria-disabled', String(disabled || busy));
        this.button.setAttribute('aria-label', disabled ? label : this.button.dataset.addAccessibleLabel);
        this.button.classList.toggle('loading', loading);
        this.spinner.classList.toggle('hidden', !loading);
        this.buttonText.textContent = label;
        if (this.sourceButton.hasAttribute('aria-haspopup')) this.button.setAttribute('aria-haspopup', 'dialog');

        const error = this.sourceError && !this.sourceError.hidden
          ? this.sourceError.querySelector('.product-form__error-message').textContent.trim()
          : '';
        this.errorMessage.textContent = error;
        this.errorMessage.hidden = !error;
        if (error) this.button.setAttribute('aria-describedby', this.errorMessage.id);
        else this.button.removeAttribute('aria-describedby');
      }
    }
  );
}
